import React, { useState, useRef, useEffect } from 'react';

interface PrimaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

interface SyncAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
}

export interface OverflowAction {
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ToolbarActionsProps {
  primary?: PrimaryAction;
  secondary?: PrimaryAction;
  onSync?: (() => void) | SyncAction | SyncAction[];
  overflow?: OverflowAction[];
}

/**
 * ToolbarActions - Standardized toolbar action buttons
 *
 * Layout: [•••] [🔄] [Secondary] [+ Primary]
 * - Overflow menu (left): Non-sync secondary actions (Import, Export, etc.)
 * - Sync button (middle): All sync/pull/push/fetch/refresh operations
 * - Secondary button: Optional secondary action button
 * - Primary button (right): Fixed-width primary action
 */
function ToolbarActions({ primary, secondary, onSync, overflow = [] }: ToolbarActionsProps) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const syncRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    if (!overflowOpen && !syncOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (overflowOpen && overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
      if (syncOpen && syncRef.current && !syncRef.current.contains(e.target as Node)) {
        setSyncOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [overflowOpen, syncOpen]);

  // Close menus on escape
  useEffect(() => {
    if (!overflowOpen && !syncOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOverflowOpen(false);
        setSyncOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [overflowOpen, syncOpen]);

  const hasOverflow = overflow && overflow.length > 0;
  const hasPrimary = primary && primary.label;

  // Determine sync button behavior
  let hasSync = false;
  let syncActions: SyncAction[] = [];
  let isSingleSync = false;

  if (onSync) {
    hasSync = true;
    if (typeof onSync === 'function') {
      // Simple function - execute on click
      syncActions = [{ label: 'Refresh', onClick: onSync }];
      isSingleSync = true;
    } else if (Array.isArray(onSync)) {
      // Array of actions
      syncActions = onSync;
      isSingleSync = onSync.length === 1;
    } else if (typeof onSync === 'object') {
      // Single action object
      syncActions = [onSync];
      isSingleSync = true;
    }
  }

  // Nothing to render
  if (!hasOverflow && !hasSync && !hasPrimary) {
    return null;
  }

  // For simple refresh (single function), execute directly on click
  // For multiple sync actions, show dropdown
  const handleSyncClick = () => {
    if (isSingleSync && typeof onSync === 'function') {
      // Simple refresh - execute immediately
      onSync();
    } else {
      // Multiple actions or object config - show dropdown
      setSyncOpen(!syncOpen);
    }
  };

  // Get title for sync button
  const getSyncTitle = () => {
    return 'Sync options';
  };

  // Check if sync button should be disabled (all actions disabled)
  const isSyncDisabled = syncActions.length > 0 && syncActions.every(a => a.disabled);

  // Check if currently syncing (label starts with "Syncing")
  const isSyncing = syncActions.length > 0 && syncActions[0]?.label?.toString().toLowerCase().includes('syncing');
  const syncingLabel = isSyncing ? syncActions[0]?.label : null;

  return (
    <div className="toolbar-actions">
      {/* Overflow Menu */}
      {hasOverflow && (
        <div className="toolbar-dropdown" ref={overflowRef}>
          <button
            className="toolbar-btn toolbar-btn-icon"
            onClick={() => setOverflowOpen(!overflowOpen)}
            title="More actions"
          >
            •••
          </button>
          {overflowOpen && (
            <div className="toolbar-dropdown-menu">
              {overflow.map((item, idx) => (
                <button
                  key={idx}
                  className="toolbar-dropdown-item"
                  onClick={() => {
                    item.onClick();
                    setOverflowOpen(false);
                  }}
                  disabled={item.disabled}
                >
                  {item.icon && <span className="toolbar-dropdown-icon">{item.icon}</span>}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sync Button - always shows dropdown, shows status when syncing */}
      {hasSync && (
        <div className="toolbar-dropdown" ref={syncRef}>
          {isSyncing ? (
            /* Show syncing status with spinning icon */
            <button
              className="toolbar-btn toolbar-btn-syncing"
              disabled={true}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                color: 'var(--color-primary)',
                cursor: 'wait'
              }}
            >
              <span style={{ animation: 'spin 1s linear infinite' }}>🔄</span>
              <span style={{ fontSize: 'var(--text-base)', whiteSpace: 'nowrap' }}>{syncingLabel}</span>
            </button>
          ) : (
            /* Normal state - show icon with dropdown */
            <>
              <button
                className="toolbar-btn toolbar-btn-icon"
                onClick={handleSyncClick}
                title={getSyncTitle()}
                disabled={isSyncDisabled}
              >
                🔄
              </button>
              {syncOpen && (
                <div className="toolbar-dropdown-menu">
                  {syncActions.map((item, idx) => (
                    <button
                      key={idx}
                      className="toolbar-dropdown-item"
                      onClick={() => {
                        item.onClick();
                        setSyncOpen(false);
                      }}
                      disabled={item.disabled}
                      title={item.tooltip || ''}
                    >
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Secondary Action */}
      {secondary && secondary.label && (
        <button
          className={`toolbar-btn toolbar-btn-secondary ${secondary.className || ''}`}
          style={secondary.style}
          onClick={secondary.onClick}
          disabled={secondary.disabled}
        >
          {secondary.label}
        </button>
      )}

      {/* Primary Action */}
      {hasPrimary && (
        <button
          className={`toolbar-btn toolbar-btn-primary ${primary.className || ''}`}
          style={primary.style}
          onClick={primary.onClick}
          disabled={primary.disabled}
        >
          {primary.label}
        </button>
      )}
    </div>
  );
}

export default ToolbarActions;
